import { useState, useEffect, useRef } from 'react';
import { MapPin, Navigation, Loader2, X } from 'lucide-react';

interface LocationPickerProps {
    onLocationSelect: (lat: number, lon: number, address?: string) => void;
    initialLat?: number;
    initialLon?: number;
    initialAddress?: string;
    placeholder?: string;
}

interface Suggestion {
    place_id: number;
    lat: string;
    lon: string;
    display_name: string;
}

export default function LocationPicker({
    onLocationSelect,
    initialAddress,
    placeholder = "Search city or area..."
}: LocationPickerProps) {
    const [query, setQuery] = useState(initialAddress || '');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            if (query.length > 2 && showSuggestions) {
                fetchSuggestions(query);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [query, showSuggestions]);

    // Click outside to close
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const fetchSuggestions = async (searchText: string) => {
        try {
            setSearchLoading(true);
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchText)}&limit=5&addressdetails=1`
            );
            if (response.ok) {
                const data = await response.json();
                setSuggestions(data);
            }
        } catch (error) {
            console.error("Error fetching suggestions:", error);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleGetCurrentLocation = () => {
        setLoading(true);
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                async (position) => {
                    const { latitude, longitude } = position.coords;
                    try {
                        // Reverse Geocode
                        const response = await fetch(
                            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`
                        );
                        if (response.ok) {
                            const data = await response.json();
                            // Construct a friendly name: City, Country or display_name
                            const addr = data.address;
                            const city = addr.city || addr.town || addr.village || addr.suburb || data.display_name.split(',')[0];
                            const friendlyName = `${city}, ${addr.country || ''}`;

                            setQuery(friendlyName);
                            onLocationSelect(latitude, longitude, friendlyName);
                        } else {
                            onLocationSelect(latitude, longitude, "Current Location");
                            setQuery("Current Location");
                        }
                    } catch (e) {
                        onLocationSelect(latitude, longitude, "Current Location");
                        setQuery("Current Location");
                    } finally {
                        setLoading(false);
                        setShowSuggestions(false);
                    }
                },
                (error) => {
                    console.error("Error getting location", error);
                    setLoading(false);
                    alert("Unable to retrieve location. Please check browser permissions.");
                }
            );
        } else {
            setLoading(false);
            alert("Geolocation is not supported by this browser.");
        }
    };

    const handleInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setQuery(e.target.value);
        setShowSuggestions(true);
    };

    const handleSelectSuggestion = (s: Suggestion) => {
        // Extract a shorter name for display input if possible, but keep full for now or parse
        // Nominatim display_name can be long. Let's try to keep it reasonable.
        setQuery(s.display_name);
        onLocationSelect(parseFloat(s.lat), parseFloat(s.lon), s.display_name);
        setShowSuggestions(false);
    };

    return (
        <div ref={wrapperRef} className="relative">
            <div className="flex gap-2">
                <div className="relative flex-1 group">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-rose-500 transition-colors" size={18} />
                    <input
                        type="text"
                        value={query}
                        onChange={handleInput}
                        onFocus={() => {
                            if (query.length > 2) setShowSuggestions(true);
                        }}
                        placeholder={placeholder}
                        className="w-full pl-10 pr-10 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-rose-500 outline-none transition-all text-gray-900 dark:text-white"
                    />
                    {searchLoading && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 size={16} className="animate-spin text-gray-400" />
                        </div>
                    )}
                    {query && (
                        <button
                            type="button"
                            onClick={() => {
                                setQuery('');
                                onLocationSelect(0, 0, ''); // Signal clear
                            }}
                            className="absolute right-10 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X size={16} />
                        </button>
                    )}
                </div>
                <button
                    type="button" // Prevent form submission
                    onClick={handleGetCurrentLocation}
                    disabled={loading}
                    title="Use my location"
                    className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors"
                >
                    {loading ? (
                        <Loader2 size={20} className="animate-spin text-rose-500" />
                    ) : (
                        <Navigation size={20} className="text-gray-600 dark:text-gray-300" />
                    )}
                </button>
            </div>

            {/* Suggestions Dropdown */}
            {showSuggestions && suggestions.length > 0 && (
                <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-100 dark:border-gray-700 max-h-60 overflow-y-auto animate-in fade-in slide-in-from-top-2">
                    {suggestions.map((suggestion) => (
                        <button
                            key={suggestion.place_id}
                            type="button"
                            onClick={() => handleSelectSuggestion(suggestion)}
                            className="w-full text-left px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700/50 flex items-start gap-3 border-b border-gray-50 dark:border-gray-700/50 last:border-0 transition-colors"
                        >
                            <MapPin className="mt-0.5 text-gray-400 shrink-0" size={16} />
                            <span className="text-sm text-gray-700 dark:text-gray-200 line-clamp-2">
                                {suggestion.display_name}
                            </span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
